import { useState, useCallback, useEffect } from 'react';
import { GameState, Judge } from '../types';

const initialGameState: GameState = {
  gameStage: 0,
  gamePhase: -3,
  players: [],
  playerNames: [],
  playerAvatars: [],
  playerRoles: [],
  playerFauls: [0, 0, 0, 0, 0, 0, 0, 0, 0, 0],
  playersLeaved: [],
  playersLeavingNow: [],
  playersLeavedPenalty: [],
  playersLeavedGP: [],
  nightCount: 0,
  redCnt: 7,
  blackCnt: 3,
  pobeda: "?",
  voteHistory: {},
  otstrel: [],
  proverkiDon: [],
  proverkiSheriff: [],
  vanga: [],
  bestMove: 0,
  gnumber: 0,
  currTable: 0,
  currPart: 0,
  isFinal: 0,
  launchTime: "0000-00-00 00:00:00",
  endTime: "0000-00-00 00:00:00",
  firstTalk: 0,
  penaltyListToday: [],
  penaltyListTomorrow: [],
  aliveDon: 1,
  aliveSheriff: 1,
};

export const useGameState = () => {
  const [gameState, setGameState] = useState<GameState>(initialGameState);
  const [pPlayers, setPPlayers] = useState<number[]>([]);
  const [pPlayerNames, setPPlayerNames] = useState<string[]>([]);
  const [pAvatars, setPAvatars] = useState<string[]>([]);
  const [judge, setJudge] = useState<Judge>({
    user_id: 0,
    name: "",
    avatar: "",
    level: 0,
  });

  const resetGameState = useCallback(() => {
    setGameState(initialGameState);
    setPPlayers([]);
    setPPlayerNames([]);
    setPAvatars([]);
    setJudge({
      user_id: 0,
      name: "",
      avatar: "",
      level: 0,
    });
  }, []);

  const updateGameState = useCallback((updates: Partial<GameState>) => {
    setGameState(prev => ({ ...prev, ...updates }));
  }, []);

  const replaceGameState = useCallback((newState: GameState) => {
    setGameState(newState);
  }, []);

  const saveGameState = useCallback(() => {
    const stateToSave = {
      gameState,
      pPlayers,
      pPlayerNames,
      pAvatars,
      judge,
      timestamp: Date.now(),
    };
    localStorage.setItem('reGameState', JSON.stringify(stateToSave));
  }, [gameState, pPlayers, pPlayerNames, pAvatars, judge]);

  const loadGameState = useCallback(() => {
    const saved = localStorage.getItem('reGameState');
    if (!saved) return false;

    try {
      const state = JSON.parse(saved);
      
      // Check if state is not too old (15 minutes)
      if (Date.now() - state.timestamp > 15 * 60 * 1000) {
        return false;
      }

      setGameState(state.gameState);
      setPPlayers(state.pPlayers || []);
      setPPlayerNames(state.pPlayerNames || []);
      setPAvatars(state.pAvatars || []);
      setJudge(state.judge || initialGameState);
      
      return true;
    } catch (error) {
      console.error('Failed to load game state:', error);
      return false;
    }
  }, []);

  const clearSavedState = useCallback(() => {
    localStorage.removeItem('reGameState');
  }, []);

  // Auto-save game state when it changes
  useEffect(() => {
    if (gameState.gameStage > 0) {
      saveGameState();
    }
  }, [gameState, saveGameState]);

  return {
    gameState,
    pPlayers,
    pPlayerNames,
    pAvatars,
    judge,
    updateGameState,
    replaceGameState,
    resetGameState,
    saveGameState,
    loadGameState,
    clearSavedState,
    setPPlayers,
    setPPlayerNames,
    setPAvatars,
    setJudge,
  };
};